import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogPositioningBehavior,
  DialogType,
  TabStyles
} from '@tableau/widgets';
import * as React from 'react';
import _ = require('underscore');
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { cssClass, divWithStyle } from '../../style';
import { ReactModalOptions } from '../../ts/services/ReactModalService';
import { buttonContainerDiv, buttonStyleSubmitting } from './styles/ExternalContentActionsStyles';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;
const MESSAGE_HEIGHT: number = 190;
const DIALOG_WIDTH: number = 500;

const CERTIFICATION_DIALOG_TEST_ID: string = 'certification-dialog';
const CERTIFICATION_DIALOG_CANCEL_TEST_ID: string = CERTIFICATION_DIALOG_TEST_ID + '-cancel';
const CERTIFICATION_DIALOG_SAVE_TEST_ID: string = CERTIFICATION_DIALOG_TEST_ID + '-save';
const CERTIFICATION_DIALOG_TOGGLE_TEST_ID: string = CERTIFICATION_DIALOG_TEST_ID + '-toggle';

const ButtonContainerDiv = buttonContainerDiv(CERTIFICATION_DIALOG_TEST_ID);

const CertificationClass = cssClass('CertificationClass', '', {
  fontWeight: 'normal',
  resize: 'none',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
  height: MESSAGE_HEIGHT,
  fontSize: TabStyles.Typography.Sizing.Default,
  marginBottom: TabStyles.Spacing.MediumSpace,
  padding: TabStyles.Spacing.DefaultSpace,
});

const HelpTextDiv = divWithStyle(CERTIFICATION_DIALOG_TEST_ID, '-help-text', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

const ActivitySpinnerWidgetStyle = divWithStyle(CERTIFICATION_DIALOG_TEST_ID, '-active-widget-spinner-style', {
  marginTop: TabStyles.Spacing.SmallSpace!
});

const checkboxStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.SmallSpace,
};

const cancelButtonStyle: React.CSSProperties = {
  marginRight: TabStyles.Spacing.MediumSpace,
};

export interface CertificationDialogProps {
  readonly initial: CertificationDialogState;
  readonly submit: (args: CertificationDialogState) => void;
  readonly cancel: () => void;
}

export interface CertificationDialogState {
  readonly certificationNote: string;
  readonly isCertified: boolean;
}

export interface CertificationDialogStateAndLifecycle extends CertificationDialogState {
  readonly isSubmitting: boolean;
  readonly isDirty: boolean;
  readonly isValid: boolean;
}

function getFormState(state: CertificationDialogState | CertificationDialogStateAndLifecycle): CertificationDialogState {
  return {
    certificationNote: state.certificationNote,
    isCertified: state.isCertified
  };
}

function checkIsValid(current: CertificationDialogState): boolean {
  return current.certificationNote.length <= MESSAGE_MAXIMUM_LENGTH;
}

function checkIsDirty(current: CertificationDialogState, initial: CertificationDialogState): boolean {
  return !_.isEqual(initial, current);
}

export class CertificationDialog extends React.Component<CertificationDialogProps, CertificationDialogStateAndLifecycle> {

  public constructor(props: CertificationDialogProps) {
    super(props);
    this.state = {
      certificationNote: props.initial.certificationNote,
      isCertified: props.initial.isCertified,
      isSubmitting: false,
      isDirty: false,
      isValid: true
    };
  }

  private handleCertificationCheckboxChange = (isCertified: boolean) => {
    this.setState((state, props) => {
      const form = getFormState(state);
      const updated: CertificationDialogState = { ...form, isCertified };
      return { ...updated, isDirty: checkIsDirty(updated, props.initial) };
    });
  }

  private onCertificationNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const certificationNote = event.target.value;
    this.setState((state, props) => {
      const form = getFormState(state);
      const updated: CertificationDialogState = { ...form, certificationNote };
      return { ...updated, isDirty: checkIsDirty(updated, props.initial), isValid: checkIsValid(updated) };
    });
  }

  private onSubmit = () => this.setState({ isSubmitting: true }, () => this.props.submit(getFormState(this.state)));

  private onCancel = () => this.setState({ isSubmitting: true }, this.props.cancel);

  public render(): React.ReactNode {
    return (
      <>
        <CheckBoxWithLabelWidget
          label={ Messages.certificationCheckboxLabel() }
          testId={ CERTIFICATION_DIALOG_TOGGLE_TEST_ID }
          checked={ this.state.isCertified }
          handleChange={ this.handleCertificationCheckboxChange }
          removeLeftMargin={ true }
          containerStyle={ checkboxStyle } />
        <HelpTextDiv>{ Messages.certificationHelpNote() }</HelpTextDiv>
        <HelpTextDiv>{ Messages.certificationNoteLabel() }</HelpTextDiv>
        <textarea onChange={ this.onCertificationNoteChange }
          className={ CertificationClass }
          value={ this.state.certificationNote } />
        <ButtonContainerDiv>
          <ButtonWidget testId={ CERTIFICATION_DIALOG_CANCEL_TEST_ID }
            buttonType={ ButtonType.Outline }
            disabled={ this.state.isSubmitting }
            handleClick={ this.onCancel }
            style={ cancelButtonStyle }>
            { Messages.cancel() }
          </ButtonWidget>
          <ButtonWidget testId={ CERTIFICATION_DIALOG_SAVE_TEST_ID }
            buttonType={ ButtonType.Go }
            disabled={ this.state.isSubmitting || !this.state.isDirty || !this.state.isValid }
            style={ this.state.isSubmitting ? buttonStyleSubmitting : undefined }
            handleClick={ this.onSubmit }>
            {
              this.state.isSubmitting ?
                <ActivitySpinnerWidgetStyle>
                  <ActivitySpinnerWidget spinnerSize={ ActivitySpinnerSize.Small } />
                </ActivitySpinnerWidgetStyle> :
                <span>{ Messages.save() }</span>
            }
          </ButtonWidget>
        </ButtonContainerDiv>
      </>
    );
  }
}

export function certificationDialogProps(props: CertificationDialogProps): ReactModalOptions {
  return {
    title: Messages.certificationLabel(),
    testId: CERTIFICATION_DIALOG_TEST_ID,
    type: DialogType.NormalWithTitleBar,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogContent: <CertificationDialog { ...props } />
  };
}
